/**
 * お問合せ自動化: データ保持タブ。
 * 仕様書: docs/01_spec/spec_inquiry_automation.md C-10
 *
 * 設定項目:
 *   - 5 コレクションの保持日数 (inquiry_sessions / inquiry_queue / qa_history_public /
 *     escalation_drafts / unreviewed_qa)
 *   - 削除スケジュール頻度 (weekly / monthly)
 *   - 動的フィールド: weekly なら曜日、monthly なら日
 *   - 実行時刻 (JST HH:MM)
 *   - 最終実行結果サマリ表示
 */
import { Calendar, Clock, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  DEFAULT_DATA_RETENTION,
  type DataRetentionSettings,
} from '@goenhub/shared';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useDataRetention,
  useInquirySettings,
  useUpdateDataRetention,
} from '@/hooks/useInquiry';

import { InquiryEmbedActions } from './InquiryEmbedActions';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

export function InquiryDataRetentionPage() {
  const settingsQuery = useInquirySettings();
  const current = useDataRetention();
  const update = useUpdateDataRetention();

  const [draft, setDraft] = useState<DataRetentionSettings | null>(null);
  const value = draft ?? current;

  // 初回ロード時に draft を current で初期化
  if (settingsQuery.data && draft === null) {
    setDraft(current);
  }

  const handleSave = async () => {
    if (!draft) return;
    if (draft.cleanupDayOfMonth < 1 || draft.cleanupDayOfMonth > 28) {
      toast.error('実行日は 1〜28 の範囲で指定してください');
      return;
    }
    try {
      await update.mutateAsync(draft);
      toast.success('データ保持設定を保存しました');
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleReset = () => {
    setDraft(DEFAULT_DATA_RETENTION);
  };

  const updateField = <K extends keyof DataRetentionSettings>(
    key: K,
    val: DataRetentionSettings[K],
  ) => {
    setDraft((prev) => ({ ...(prev ?? current), [key]: val }));
  };

  if (settingsQuery.isLoading || !draft) {
    return (
      <Screen title="データ保持" actions={<InquiryEmbedActions />}>
        <div className="py-8 flex justify-center"><Spinner /></div>
      </Screen>
    );
  }

  const formatDateTime = (iso: string | { toDate(): Date } | null): string => {
    if (!iso) return '未実行';
    try {
      const d = typeof iso === 'string' ? new Date(iso) : iso.toDate();
      return d.toLocaleString('ja-JP');
    } catch {
      return '不明';
    }
  };

  return (
    <Screen
      title="データ保持"
      description="お問合せ自動化のデータを自動的にクリーンアップする保持期間と削除スケジュールを設定します"
      actions={<InquiryEmbedActions />}
    >
      <div className="max-w-3xl space-y-6">
        {/* 保持期間セクション */}
        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Trash2 className="size-4 text-[var(--text-muted)]" /> 保持期間（日数）
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            各コレクションを何日間保持するか設定します。期限切れデータは自動削除されます。
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RetentionField
              label="お問合せセッション"
              description="ユーザーごとの会話履歴 (chat_messages も連鎖削除)"
              value={value.inquirySessionsDays}
              onChange={(v) => updateField('inquirySessionsDays', v)}
              defaultValue={365}
            />
            <RetentionField
              label="事務承認キュー (解決済み)"
              description="完了した承認案件の保持期間"
              value={value.inquiryQueueResolvedDays}
              onChange={(v) => updateField('inquiryQueueResolvedDays', v)}
              defaultValue={180}
            />
            <RetentionField
              label="QA 履歴 (Public 学習データ)"
              description="AI 学習データとしての保持期間。LINE Bot 由来は対象外"
              value={value.qaHistoryPublicDays}
              onChange={(v) => updateField('qaHistoryPublicDays', v)}
              defaultValue={1825}
            />
            <RetentionField
              label="エスカレ下書き"
              description="承認待ち AI 下書きの保持期間"
              value={value.escalationDraftsDays}
              onChange={(v) => updateField('escalationDraftsDays', v)}
              defaultValue={30}
            />
            <RetentionField
              label="未承認 QA"
              description="adminReviewed=false な QA の保持期間"
              value={value.unreviewedQaDays}
              onChange={(v) => updateField('unreviewedQaDays', v)}
              defaultValue={90}
            />
          </div>
        </section>

        {/* スケジュールセクション */}
        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <Calendar className="size-4 text-[var(--text-muted)]" /> 削除スケジュール
          </h3>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            クリーンアップを実行する頻度と時刻を設定します。
          </p>

          <div className="space-y-3">
            <div>
              <Label>頻度</Label>
              <div className="flex gap-3 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="frequency"
                    value="monthly"
                    checked={value.cleanupFrequency === 'monthly'}
                    onChange={() => updateField('cleanupFrequency', 'monthly')}
                  />
                  <span className="text-sm">毎月</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="frequency"
                    value="weekly"
                    checked={value.cleanupFrequency === 'weekly'}
                    onChange={() => updateField('cleanupFrequency', 'weekly')}
                  />
                  <span className="text-sm">毎週</span>
                </label>
              </div>
            </div>

            {/* 動的フィールド */}
            {value.cleanupFrequency === 'monthly' ? (
              <div>
                <Label>実行日（毎月）</Label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={value.cleanupDayOfMonth}
                  onChange={(e) =>
                    updateField('cleanupDayOfMonth', Math.max(1, Math.min(28, Number(e.target.value))))
                  }
                  className="w-32"
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  1〜28 の範囲。月をまたぐ問題を避けるため 29-31 日は選択できません
                </p>
              </div>
            ) : (
              <div>
                <Label>実行曜日（毎週）</Label>
                <div className="flex gap-1 mt-1">
                  {WEEKDAYS.map((label, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => updateField('cleanupDayOfWeek', idx)}
                      className={`w-10 h-10 rounded text-sm border ${
                        value.cleanupDayOfWeek === idx
                          ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                          : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border-[var(--border)] hover:border-[var(--accent)]'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>実行時刻 (JST)</Label>
              <Input
                type="time"
                value={value.cleanupTimeJst}
                onChange={(e) => updateField('cleanupTimeJst', e.target.value)}
                className="w-32"
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">
                推奨: 03:00 (トラフィック低帯)
              </p>
            </div>
          </div>
        </section>

        {/* 最終実行結果 */}
        <section className="rounded bg-[var(--bg-surface)] border border-[var(--border)] p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Clock className="size-4 text-[var(--text-muted)]" /> 最終実行結果
          </h3>
          <div className="text-sm space-y-1">
            <div>
              <span className="text-[var(--text-muted)]">前回実行:</span>{' '}
              {formatDateTime(current.lastCleanupAt)}
            </div>
            {current.lastCleanupResult ? (
              <>
                <div>
                  <span className="text-[var(--text-muted)]">削除件数合計:</span>{' '}
                  <strong>{current.lastCleanupResult.totalDeleted}</strong>
                </div>
                <div className="text-xs text-[var(--text-muted)] grid grid-cols-2 gap-x-4 mt-2">
                  <div>セッション: {current.lastCleanupResult.inquirySessions}</div>
                  <div>メッセージ: {current.lastCleanupResult.chatMessages}</div>
                  <div>キュー: {current.lastCleanupResult.inquiryQueue}</div>
                  <div>QA: {current.lastCleanupResult.qaHistory}</div>
                  <div>エスカレ下書き: {current.lastCleanupResult.escalationDrafts}</div>
                  <div>未承認QA: {current.lastCleanupResult.unreviewedQa}</div>
                </div>
                {current.lastCleanupResult.errorMessage && (
                  <div className="text-xs text-red-500 mt-2">
                    エラー: {current.lastCleanupResult.errorMessage}
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-[var(--text-muted)]">まだ実行されていません</div>
            )}
          </div>
        </section>

        {/* アクションボタン */}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleReset}>
            初期値に戻す
          </Button>
          <Button onClick={handleSave} disabled={update.isPending}>
            保存
          </Button>
        </div>
      </div>
    </Screen>
  );
}

function RetentionField({
  label,
  description,
  value,
  onChange,
  defaultValue,
}: {
  label: string;
  description: string;
  value: number;
  onChange: (v: number) => void;
  defaultValue: number;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={1}
          max={3650}
          value={value}
          onChange={(e) => onChange(Math.max(1, Number(e.target.value)))}
          className="w-28"
        />
        <span className="text-xs text-[var(--text-muted)]">日</span>
        {value !== defaultValue && (
          <button
            type="button"
            onClick={() => onChange(defaultValue)}
            className="text-xs text-[var(--text-muted)] hover:text-[var(--accent)] underline"
          >
            初期値 {defaultValue}
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--text-muted)] mt-0.5">{description}</p>
    </div>
  );
}
